import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowRightLeft, Check, X, Loader2, AlertTriangle, ChevronDown, Clock, Package } from 'lucide-react';
import apiService from '../services/api';
import { useAuth } from '../context/AuthContext';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from '../components/common/PasswordRequirements';

interface TransferPreview {
  transfer_id: number;
  status: string;
  expires_at: string;
  source_agency_id: string;
  workspace: {
    id: number;
    name: string;
    domain: string;
    tier_name: string;
    is_lifetime: boolean;
    lifetime_code_count: number;
  };
  source: {
    email: string;
    name: string;
  };
  snapshot: {
    subscription_status: string;
    tier_name: string;
    is_lifetime: boolean;
    lifetime_code_count: number;
    lifetime_stacked_named_users: number;
  };
target_agencies: Array<{
    agency_id: string;
    name: string;
    role: string;
  }>;
  requires_agency_selection: boolean;
  target_user_exists: boolean;
}

const TransferAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgencyId, setSelectedAgencyId] = useState<string>('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState<{ workspace_name: string; new_status: string; trial_ends_at: string | null } | null>(null);
const { policy: passwordPolicy } = usePasswordPolicy();

  // New-account registration (recipient has no DashboardFox account yet)
  const [regName, setRegName] = useState('');
  const [regCompany, setRegCompany] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [registering, setRegistering] = useState(false);

  // Hover states
  const [acceptHovered, setAcceptHovered] = useState(false);
  const [declineHovered, setDeclineHovered] = useState(false);
  const [loginHovered, setLoginHovered] = useState(false);
  const [dashboardHovered, setDashboardHovered] = useState(false);

  useEffect(() => {
    loadPreview();
  }, [token]);

  useEffect(() => {
    if (preview && preview.target_agencies.length === 1) {
      setSelectedAgencyId(preview.target_agencies[0].agency_id);
    }
  }, [preview]);

const loadPreview = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/transfers/${token}/preview`);
      setPreview(response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load transfer details');
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterAndAccept = async () => {
    if (!regName.trim()) { setError('Please enter your name'); return; }
const { meetsClientChecks: pwOk } = evaluatePassword(regPassword, passwordPolicy);
    if (!pwOk) { setError(`Password must be at least ${passwordPolicy.minLength} characters`); return; }
    if (regPassword !== regConfirm) { setError('Passwords do not match'); return; }

    setRegistering(true);
    setError(null);
    try {
      const response = await apiService.post(`/transfers/${token}/accept-new`, {
        name: regName.trim(),
        company_name: regCompany.trim() || undefined,
        password: regPassword,
      });
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      setSuccessData(response.data);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to create account and accept transfer');
      setRegistering(false);
    }
  };

  const handleAccept = async () => {
    if (preview?.requires_agency_selection && !selectedAgencyId) {
      setError('Please select an agency');
      return;
    }

    setAccepting(true);
    setError(null);

    try {
      const response = await apiService.post(`/transfers/${token}/accept`, {
        target_agency_id: selectedAgencyId || undefined,
      });
      setSuccessData(response.data);
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to accept transfer');
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('Are you sure you want to decline this transfer?')) return;

    setDeclining(true);
    setError(null);

    try {
      await apiService.post(`/transfers/${token}/cancel`, { reason: 'Declined by recipient' });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to decline transfer');
    } finally {
      setDeclining(false);
    }
  };

  const formatTimeRemaining = (expiresAt: string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffMs = expires.getTime() - now.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffMs < 0) return 'Expired';
    if (diffHours >= 24) return `${Math.floor(diffHours / 24)}d ${diffHours % 24}h remaining`;
    if (diffHours > 0) return `${diffHours}h ${diffMins}m remaining`;
    return `${diffMins}m remaining`;
  };

  // Styles
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '520px',
  };

  const iconBoxStyle: React.CSSProperties = {
    width: '64px',
    height: '64px',
    borderRadius: 'var(--radius-lg)',
    background: success ? 'var(--v2-success-bg)' : 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    boxShadow: success ? 'none' : '0 4px 12px rgba(79, 70, 229, 0.3)',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
    textAlign: 'center',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 24px 0',
    textAlign: 'center',
    lineHeight: 1.6,
  };

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    marginBottom: '20px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 0',
  };

  const alertStyle = (type: 'error' | 'warning' | 'success' | 'info'): React.CSSProperties => {
    const colors = {
      error: { bg: 'var(--v2-error-bg)', border: 'var(--v2-error)', text: 'var(--v2-error)' },
      warning: { bg: 'var(--v2-warning-bg)', border: '#fcd34d', text: '#92400e' },
      success: { bg: 'var(--v2-success-bg)', border: '#86efac', text: 'var(--v2-success-dark)' },
      info: { bg: 'var(--v2-info-bg)', border: '#93c5fd', text: 'var(--v2-primary)' },
    };
    const c = colors[type];
    return {
      padding: '14px 16px',
      borderRadius: 'var(--radius-md)',
      borderLeft: `4px solid ${c.border}`,
      backgroundColor: c.bg,
      color: c.text,
      fontSize: '0.875rem',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
    };
  };

  const getPrimaryBtnStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 20px',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const getSecondaryBtnStyle = (hovered: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '12px 20px',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

// Loading state — but never block a brand-new recipient on the auth bootstrap. A
  // no-account recipient (target_user_exists === false) is logged out by definition, so a
  // stale token wedging `initAuth` shouldn't trap them on the spinner. Render their
  // register form as soon as the preview is in.
  const newRecipient = preview && preview.target_user_exists === false;
  if (loading || (authLoading && !newRecipient)) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={pageStyle}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 0' }}>
              <Loader2 size={48} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
              <p style={{ ...subtitleStyle, marginBottom: 0 }}>Loading transfer details...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state (no preview)
  if (error && !preview) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={iconBoxStyle}>
            <AlertTriangle size={32} strokeWidth={1.5} color="#ffffff" />
          </div>
          <h1 style={titleStyle}>Transfer Unavailable</h1>
          <div style={alertStyle('error')}>
            <AlertTriangle size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{error}</span>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            onMouseEnter={() => setDashboardHovered(true)}
            onMouseLeave={() => setDashboardHovered(false)}
            style={{ ...getPrimaryBtnStyle(dashboardHovered, false), width: '100%' }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success && successData) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={{ ...iconBoxStyle, background: 'var(--v2-success-bg)' }}>
            <Check size={32} strokeWidth={2} color="var(--v2-success)" />
          </div>
          <h1 style={titleStyle}>Transfer Complete!</h1>
          <p style={subtitleStyle}>
            "{successData.workspace_name}" has been transferred to your account.
          </p>

          <div style={alertStyle('success')}>
            <Check size={18} strokeWidth={2} style={{ flexShrink: 0, marginTop: '2px' }} />
            <div>
              {successData.new_status === 'lifetime' ? (
                <span>This workspace has a <strong>lifetime license</strong> and is ready to use.</span>
              ) : (
                <span>
                  You have a <strong>7-day trial</strong> to set up billing.
                  {successData.trial_ends_at && (
                    <> Trial ends {new Date(successData.trial_ends_at).toLocaleDateString()}.</>
                  )}
                </span>
              )}
            </div>
          </div>

          <button
            onClick={() => navigate('/dashboard')}
            onMouseEnter={() => setDashboardHovered(true)}
            onMouseLeave={() => setDashboardHovered(false)}
            style={{ ...getPrimaryBtnStyle(dashboardHovered, false), width: '100%' }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

// Not logged in state
  if (!user) {
    // Recipient has no account yet → let them create one and claim the workspace.
    if (preview && preview.target_user_exists === false) {
const { meetsClientChecks: allReqMet } = evaluatePassword(regPassword, passwordPolicy);
      const passwordsMatch = regPassword === regConfirm && regConfirm.length > 0;
      const regDisabled = registering || !regName.trim() || !allReqMet || !passwordsMatch;

      const rLabel: React.CSSProperties = { display: 'block', fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)', marginBottom: '6px' };
      const rInput: React.CSSProperties = { width: '100%', padding: '10px 12px', fontSize: '0.9375rem', border: '1px solid var(--v2-border)', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--v2-surface)', color: 'var(--v2-text)', outline: 'none', boxSizing: 'border-box', marginBottom: '16px' };
      const rReq = (ok: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', color: ok ? 'var(--v2-success)' : 'var(--v2-text-muted)' });
      const circle = <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid var(--v2-border)', display: 'inline-block', flexShrink: 0 }} />;

      return (
        <>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={pageStyle}>
            <div style={cardStyle}>
              <div style={iconBoxStyle}>
                <ArrowRightLeft size={32} strokeWidth={1.5} color="#ffffff" />
              </div>
              <h1 style={titleStyle}>Claim Your Workspace</h1>
              <p style={subtitleStyle}>
                {preview.source.name || preview.source.email} is transferring "{preview.workspace.name}" to you. Create your account to claim it.
              </p>

              {error && (
                <div style={alertStyle('error')}>
                  <AlertTriangle size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <span>{error}</span>
                </div>
              )}

              <div style={infoBoxStyle}>
                <div style={infoRowStyle}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Workspace</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{preview.workspace.name}</span>
                </div>
                <div style={infoRowStyle}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>From</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{preview.source.email}</span>
                </div>
              </div>

              <label style={rLabel}>Your Name</label>
              <input type="text" value={regName} onChange={(e) => setRegName(e.target.value)} disabled={registering} placeholder="Your full name" style={rInput} autoFocus />

              <label style={rLabel}>Company Name <span style={{ color: 'var(--v2-text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input type="text" value={regCompany} onChange={(e) => setRegCompany(e.target.value)} disabled={registering} placeholder="Your company" style={rInput} />

              <label style={rLabel}>Password</label>
              <input type="password" value={regPassword} onChange={(e) => setRegPassword(e.target.value)} disabled={registering} autoComplete="new-password" style={rInput} />

<PasswordRequirements
                password={regPassword}
                policy={passwordPolicy}
                columns={1}
                style={{ marginBottom: '16px' }}
              />

              <label style={rLabel}>Confirm Password</label>
              <input type="password" value={regConfirm} onChange={(e) => setRegConfirm(e.target.value)} disabled={registering} autoComplete="new-password" style={{ ...rInput, marginBottom: '8px' }} />
              {regConfirm && !passwordsMatch && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '0 0 16px 0' }}>Passwords do not match</p>
              )}
              {passwordsMatch && (
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-success)', margin: '0 0 16px 0', display: 'flex', alignItems: 'center', gap: '4px' }}><Check size={14} strokeWidth={2} /> Passwords match</p>
              )}

              <button onClick={handleRegisterAndAccept} disabled={regDisabled} style={{ ...getPrimaryBtnStyle(false, regDisabled), width: '100%' }}>
                {registering ? <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={18} strokeWidth={2} />}
                Create Account &amp; Claim Workspace
              </button>

              <p style={{ fontSize: '0.75rem', color: 'var(--v2-text-muted)', textAlign: 'center', marginTop: '16px', marginBottom: 0 }}>
                You'll become the owner of "{preview.workspace.name}" with a fresh trial to set up billing.
              </p>
            </div>
          </div>
        </>
      );
    }

    // Recipient already has an account → prompt login.
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={iconBoxStyle}>
            <ArrowRightLeft size={32} strokeWidth={1.5} color="#ffffff" />
          </div>
          <h1 style={titleStyle}>Workspace Transfer</h1>
          <p style={subtitleStyle}>
            {preview?.source.name || preview?.source.email} wants to transfer "{preview?.workspace.name}" to you.
          </p>

          <div style={infoBoxStyle}>
            <div style={infoRowStyle}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Workspace</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{preview?.workspace.name}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>From</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{preview?.source.email}</span>
            </div>
          </div>

          <div style={alertStyle('info')}>
            <Clock size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>Please log in to accept or decline this transfer.</span>
          </div>

          <button
            onClick={() => navigate(`/auth?redirect=/transfers/accept/${token}`)}
            onMouseEnter={() => setLoginHovered(true)}
            onMouseLeave={() => setLoginHovered(false)}
            style={{ ...getPrimaryBtnStyle(loginHovered, false), width: '100%' }}
          >
            Log in to Continue
          </button>
        </div>
      </div>
    );
  }

  // Main accept/decline view
  const selectedAgency = preview?.target_agencies.find(a => a.agency_id === selectedAgencyId);

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={pageStyle}>
        <div style={cardStyle} onClick={() => setDropdownOpen(false)}>
          <div style={iconBoxStyle}>
            <ArrowRightLeft size={32} strokeWidth={1.5} color="#ffffff" />
          </div>
          <h1 style={titleStyle}>Accept Workspace Transfer?</h1>
          <p style={subtitleStyle}>
            {preview?.source.name || preview?.source.email} wants to transfer ownership of this workspace to you.
          </p>

          {error && (
            <div style={alertStyle('error')}>
              <AlertTriangle size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>{error}</span>
            </div>
          )}

          {/* Workspace Details */}
          <div style={infoBoxStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--v2-border)' }}>
              <Package size={18} strokeWidth={1.5} color="var(--v2-primary)" />
              <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-text)' }}>{preview?.workspace.name}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Domain</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)', fontFamily: 'var(--font-mono)' }}>
                {preview?.workspace.domain}.dashboardfox.app
              </span>
            </div>
            <div style={infoRowStyle}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Plan</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>
                {preview?.workspace.tier_name || 'Starter'}
                {preview?.workspace.is_lifetime && (
                  <span style={{ marginLeft: '8px', fontSize: '0.6875rem', fontWeight: 600, color: '#8b5cf6', backgroundColor: '#ede9fe', padding: '2px 6px', borderRadius: '4px' }}>
                    LIFETIME
                  </span>
                )}
              </span>
            </div>
            <div style={infoRowStyle}>
              <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>From</span>
              <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{preview?.source.email}</span>
            </div>
          </div>

          {/* Time remaining */}
          <div style={{ ...alertStyle('warning'), marginBottom: '20px' }}>
            <Clock size={18} strokeWidth={1.5} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{formatTimeRemaining(preview?.expires_at || '')}</span>
          </div>

          {/* Agency selection (if multiple) */}
          {preview?.requires_agency_selection && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '0.6875rem', fontWeight: 600, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                Select Destination Agency
              </label>
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen(!dropdownOpen);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    backgroundColor: 'var(--v2-surface)',
                    border: `1px solid ${dropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                    borderRadius: 'var(--radius-md)',
                    fontSize: '0.875rem',
                    color: selectedAgency ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'border-color 0.15s, box-shadow 0.15s',
                    boxShadow: dropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                  }}
                >
                  <span>{selectedAgency ? selectedAgency.name : 'Choose an agency...'}</span>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.5}
                    color="var(--v2-text-muted)"
                    style={{ transition: 'transform 0.15s ease', transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)' }}
                  />
                </button>

                {dropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'var(--v2-surface)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: 'var(--shadow-lg)',
                      zIndex: 10,
                      padding: '4px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {preview.target_agencies.map((agency) => (
                      <button
                        key={agency.agency_id}
                        type="button"
                        onClick={() => {
                          setSelectedAgencyId(agency.agency_id);
                          setDropdownOpen(false);
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 12px',
                          backgroundColor: selectedAgencyId === agency.agency_id ? 'var(--v2-primary-light)' : 'transparent',
                          border: 'none',
                          textAlign: 'left',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          borderRadius: 'var(--radius-sm)',
                          transition: 'background-color 0.1s',
                        }}
                        onMouseOver={(e) => {
                          if (selectedAgencyId !== agency.agency_id) {
                            e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
                          }
                        }}
                        onMouseOut={(e) => {
                          if (selectedAgencyId !== agency.agency_id) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }
                        }}
                      >
                        <span style={{ fontWeight: 500, color: 'var(--v2-text)', fontSize: '0.875rem' }}>{agency.name}</span>
                        <span style={{ fontSize: '0.75rem', color: 'var(--v2-text-tertiary)', textTransform: 'capitalize' }}>({agency.role})</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* What happens info */}
          <div style={{ ...infoBoxStyle, backgroundColor: 'var(--v2-surface)', border: '1px dashed var(--v2-border)', marginBottom: '24px' }}>
            <div style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', lineHeight: 1.7 }}>
<p style={{ margin: '0 0 8px 0' }}>
                <strong>What you'll receive:</strong><br />
                Workspace infrastructure, custom domains
              </p>
<p style={{ margin: 0 }}>
                {preview?.workspace.is_lifetime ? (
                  <>
                    <strong>What transfers with workspace:</strong><br />
                    Lifetime license{preview?.workspace.lifetime_code_count > 1 ? ` (${preview?.workspace.lifetime_code_count} stacked codes)` : ''} and all stacked limits.
                  </>
                ) : (
                  <>
                    <strong>What you'll need:</strong><br />
                    Set up billing within 7 days to continue service.
                  </>
                )}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleDecline}
              disabled={declining || accepting}
              onMouseEnter={() => setDeclineHovered(true)}
              onMouseLeave={() => setDeclineHovered(false)}
              style={getSecondaryBtnStyle(declineHovered)}
            >
              {declining ? (
                <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <X size={18} strokeWidth={1.5} />
              )}
              Decline
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting || declining || (preview?.requires_agency_selection && !selectedAgencyId)}
              onMouseEnter={() => setAcceptHovered(true)}
              onMouseLeave={() => setAcceptHovered(false)}
style={getPrimaryBtnStyle(acceptHovered, accepting || declining || !!(preview?.requires_agency_selection && !selectedAgencyId))}
            >
              {accepting ? (
                <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Check size={18} strokeWidth={2} />
              )}
              Accept Transfer
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default TransferAcceptPage;
